/** Where the markdown loader's status bar belongs on screen. */

/** Gap the bar keeps from the bottom edge of the scrolling viewport while it's pinned there. */
export const VIEWPORT_INSET_PX = 16
/** How far the bar overlaps the end of the content while it's following it. */
export const CONTENT_OVERLAP_PX = 12

export interface ContentEnd {
   /** Viewport y the bar should hang under. */
   y: number
   /** Whether there is any text to hang under, since an empty document gets no overlap. */
   hasContent: boolean
}

export interface BarPlacement {
   /** Where to put the bar's top, relative to the zero-height anchor at the end of the document. */
   top: number
   /** Whether the bar is held above the viewport's bottom edge rather than sitting under the content. */
   isPinned: boolean
}

/**
 * Places the bar just under the end of the document, or pinned above the viewport's bottom edge.
 * @param params One frame's viewport measurements; `contentEnd` is undefined with no editor to measure.
 * @returns The bar's top, relative to the anchor, and whether it ended up pinned.
 */
export const computeBarPlacement = (params: {
   anchorTop: number
   contentEnd: ContentEnd | undefined
   scrollportBottom: number
   barHeight: number
}): BarPlacement => {
   const { anchorTop, contentEnd, scrollportBottom, barHeight } = params
   const pinnedTop = scrollportBottom - VIEWPORT_INSET_PX - barHeight
   const followTop = contentEnd === undefined
      ? pinnedTop
      : contentEnd.y - (contentEnd.hasContent ? CONTENT_OVERLAP_PX : 0)
   return { top: Math.min(followTop, pinnedTop) - anchorTop, isPinned: pinnedTop <= followTop }
}
